import { Divider, List, ListSubheader } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import StarIcon from '@mui/icons-material/Star';
import TimelineIcon from '@mui/icons-material/Timeline';
import PauseCircleFilledIcon from '@mui/icons-material/PauseCircleFilled';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BarChartIcon from '@mui/icons-material/BarChart';
import RouteIcon from '@mui/icons-material/Route';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import MapIcon from '@mui/icons-material/Map';
import LinkIcon from '@mui/icons-material/Link';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import NotesIcon from '@mui/icons-material/Notes';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import DescriptionIcon from '@mui/icons-material/Description';
import ExploreIcon from '@mui/icons-material/Explore';
import SettingsIcon from '@mui/icons-material/Settings';
import SpeedIcon from '@mui/icons-material/Speed';
import NatureIcon from '@mui/icons-material/Nature';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useLocation } from 'react-router-dom';
import { useTranslation } from '../../common/components/LocalizationProvider';
import { useAdministrator, useRestriction } from '../../common/util/permissions';
import MenuItem from '../../common/components/MenuItem';

// Styles defined inside component to use premium tokens
const useStyles = makeStyles()(() => ({
  // Base class - tokens applied via sx prop
  subheader: {
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    lineHeight: '28px',
  },
}));

const ReportsMenu = () => {
  const { classes } = useStyles();
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const location = useLocation();

  const admin = useAdministrator();
  const readonly = useRestriction('readonly');

  const buildLink = (path) => {
    const sourceParams = new URLSearchParams(location.search);
    const deviceIds = sourceParams.getAll('deviceId');
    const groupIds = sourceParams.getAll('groupId');
    if (!deviceIds.length && !groupIds.length) {
      return path;
    }
    const params = new URLSearchParams();
    if (path === '/reports/chart' || path === '/reports/route' || path === '/replay') {
      const [firstDeviceId] = deviceIds;
      if (firstDeviceId != null) {
        params.append('deviceId', firstDeviceId);
      }
    } else {
      deviceIds.forEach((deviceId) => params.append('deviceId', deviceId));
      groupIds.forEach((groupId) => params.append('groupId', groupId));
    }
    const search = params.toString();
    return search ? `${path}?${search}` : path;
  };

  // Premium subheader styling with tokens
  const subheaderSx = {
    backgroundColor: tokens.surface.card,
    color: tokens.text.secondary,
    paddingLeft: 2,
    borderBottom: `1px solid ${tokens.border.subtle}`,
  };

  return (
    <>
      {/* === RELATÓRIOS PADRÃO (Traccar) === */}
      <List
        subheader={
          <ListSubheader component="div" className={classes.subheader} sx={subheaderSx}>
            <DescriptionIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle', color: tokens.accent.primary }} />
            {t('reportTitle')}
          </ListSubheader>
        }
        disablePadding
      >
        <MenuItem
          title={t('reportCombined')}
          link={buildLink('/reports/combined')}
          icon={<StarIcon />}
          selected={location.pathname === '/reports/combined'}
        />
        <MenuItem
          title={t('reportEvents')}
          link={buildLink('/reports/events')}
          icon={<NotificationsActiveIcon />}
          selected={location.pathname === '/reports/events'}
        />
        <MenuItem
          title={t('reportTrips')}
          link={buildLink('/reports/trips')}
          icon={<PlayCircleFilledIcon />}
          selected={location.pathname === '/reports/trips'}
        />
        <MenuItem
          title={t('reportStops')}
          link={buildLink('/reports/stops')}
          icon={<PauseCircleFilledIcon />}
          selected={location.pathname === '/reports/stops'}
        />
        <MenuItem
          title={t('reportSummary')}
          link={buildLink('/reports/summary')}
          icon={<FormatListBulletedIcon />}
          selected={location.pathname === '/reports/summary'}
        />
        <MenuItem
          title={t('reportPositions')}
          link={buildLink('/reports/route')}
          icon={<TimelineIcon />}
          selected={location.pathname === '/reports/route'}
        />
        <MenuItem
          title={t('reportChart')}
          link={buildLink('/reports/chart')}
          icon={<TrendingUpIcon />}
          selected={location.pathname === '/reports/chart'}
        />
      </List>

      {/* === VISUALIZAÇÃO NO MAPA === */}
      <List
        subheader={
          <ListSubheader component="div" className={classes.subheader} sx={subheaderSx}>
            <ExploreIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle', color: tokens.accent.primary }} />
            {t('mapTitle')}
          </ListSubheader>
        }
        disablePadding
      >
        <MenuItem
          title={t('reportReplay')}
          link={buildLink('/replay')}
          icon={<RouteIcon />}
          selected={location.pathname === '/replay'}
        />
        <MenuItem
          title={t('reportHeatmap')}
          link={buildLink('/heatmap')}
          icon={<WhatshotIcon />}
          selected={location.pathname === '/heatmap'}
        />
        <MenuItem
          title={t('mapEventTitle')}
          link={buildLink('/eventmap')}
          icon={<MapIcon />}
          selected={location.pathname === '/eventmap'}
        />
        <MenuItem
          title={t('correlationTitle')}
          link={buildLink('/reports/correlation')}
          icon={<LinkIcon />}
          selected={location.pathname === '/reports/correlation'}
        />
      </List>

      {/* === GESTÃO DE FROTA === */}
      <List
        subheader={
          <ListSubheader component="div" className={classes.subheader} sx={subheaderSx}>
            <AssessmentIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle', color: tokens.accent.primary }} />
            {t('reportManagement') || 'Gestão'}
          </ListSubheader>
        }
        disablePadding
      >
        <MenuItem
          title={t('reportSpeeding')}
          link={buildLink('/reports/management/speeding')}
          icon={<SpeedIcon />}
          selected={location.pathname === '/reports/management/speeding'}
        />
        <MenuItem
          title={t('reportEcoDriving')}
          link={buildLink('/reports/management/eco')}
          icon={<NatureIcon />}
          selected={location.pathname === '/reports/management/eco'}
        />
        <MenuItem
          title={t('reportTripsAnalysis')}
          link={buildLink('/reports/management/trips')}
          icon={<RouteIcon />}
          selected={location.pathname === '/reports/management/trips'}
        />
        <MenuItem
          title={t('reportPositionsHistory')}
          link={buildLink('/reports/management/positions')}
          icon={<HistoryIcon />}
          selected={location.pathname === '/reports/management/positions'}
        />
        <MenuItem
          title={t('reportDriverPerformance')}
          link={buildLink('/reports/management/driver')}
          icon={<PersonIcon />}
          selected={location.pathname === '/reports/management/driver'}
        />
      </List>

      {/* === SISTEMA === */}
      <List
        subheader={
          <ListSubheader component="div" className={classes.subheader} sx={subheaderSx}>
            <SettingsIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle', color: tokens.accent.primary }} />
            {t('sharedExtra')}
          </ListSubheader>
        }
        disablePadding
      >
        <MenuItem
          title={t('sharedLogs')}
          link="/reports/logs"
          icon={<NotesIcon />}
          selected={location.pathname === '/reports/logs'}
        />
        {!readonly && (
          <MenuItem
            title={t('reportScheduled')}
            link="/reports/scheduled"
            icon={<EventRepeatIcon />}
            selected={location.pathname === '/reports/scheduled'}
          />
        )}
        {admin && (
          <>
            <MenuItem
              title={t('statisticsTitle')}
              link="/reports/statistics"
              icon={<BarChartIcon />}
              selected={location.pathname === '/reports/statistics'}
            />
            <MenuItem
              title={t('reportAudit')}
              link="/reports/audit"
              icon={<VerifiedUserIcon />}
              selected={location.pathname === '/reports/audit'}
            />
          </>
        )}
      </List>
    </>
  );
};

export default ReportsMenu;
